import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { EditorialImage } from "@/components/Placeholder";
import { PullQuotePrimary, PullQuoteInline } from "@/components/PullQuote";
import { QUOTES } from "@/lib/quotes";
import { useSEO, SEO } from "@/lib/seo";

const VENTURE_SEO = {
    "valanti-labs": SEO.labs,
    "alati": SEO.alati,
    "music": SEO.music,
};

const IMG = {
    warmShadow: "/images/warm-shadow.jpg",
    microphone: "/images/microphone.jpg",
    soap: "/images/alati-soap.jpg",
    stage: "/images/stage.jpg",
};

const VENTURES = {
    "valanti-labs": {
        index: "01",
        eyebrow: "Valanti Labs",
        title: "Rebuilding confidence through movement.",
        intro:
            "Valanti Labs is an AI and movement-technology company inspired by the loss of confidence many people experience in their bodies during treatment, recovery and physical change.",
        body: [
            "During treatment and recovery, I experienced first-hand how quickly trust in your own body can disappear. Stairs become questions. Carrying a child becomes a calculation. Movement that was once automatic becomes self-conscious.",
            "Valanti Labs is developing intelligent, video-based movement technology designed to help people better understand movement using an ordinary smartphone. No specialised cameras. No wearables. Just the potential to make meaningful movement insight more accessible.",
            "We are not a medical device, a diagnostic tool, or a substitute for clinical advice. Valanti Labs exists to support greater awareness and confidence — and to remove some of the friction between people and the movement they want to return to.",
        ],
        quote: QUOTES.bodyWorth,
        image: IMG.warmShadow,
        tint: "oxblood",
    },
    alati: {
        index: "02",
        eyebrow: "ALATI",
        title: "Care, tradition and a business built with my son.",
        intro:
            "ALATI is a mother-and-son soap business. It brings together Greek heritage, traditional craftsmanship, carefully considered ingredients, and the slow work of making something honest with your hands.",
        body: [
            "After my treatment, I became more attentive to what touches my skin. Not in a way that promised anything — simply in a way that paid attention. ALATI grew from that attentiveness, and from the work my son Adonis and I do together.",
            "Each bar is shaped slowly. The recipes draw on Greek tradition. The ingredients are chosen with care. We are not making medical claims. We are making soap — and making it together.",
        ],
        image: IMG.soap,
        position: "35% center",
        alt: "Three handmade ALATI soap bars stacked on stone with rosemary and dried calendula on a soft linen backdrop.",
        tint: "dark",
    },
    music: {
        index: "03",
        eyebrow: "Music",
        title: "Finding my voice again.",
        intro:
            "Music was part of my identity for most of my life. Cancer silenced it for a time. Grief called it back.",
        body: [
            "My return to music is not about pretending nothing happened. It is about allowing the woman I am now to stand on stage with everything she has lived through.",
            "Some of the songs are old. Some are new. Some belong to the years I lost. Some belong to the years I am still building.",
        ],
        quote: QUOTES.survivalIdentity,
        image: IMG.microphone,
        tint: "dark",
    },
};

const ORDER = ["valanti-labs", "alati", "music"];

export default function Building() {
    const { venture } = useParams();
    if (venture && VENTURES[venture]) return <VentureDetail slug={venture} />;
    return <BuildingOverview />;
}

function BuildingOverview() {
    useSEO(SEO.building);
    return (
        <main>
            <section className="bg-charcoal text-ivory pt-36 md:pt-44 pb-20 md:pb-28">
                <div className="container-editorial">
                    <p className="eyebrow text-gold/90">What I'm Building</p>
                    <h1 className="display-1 mt-8 max-w-4xl text-ivory">
                        I am turning lived experience <span className="italic text-gold/95">into something useful.</span>
                    </h1>
                    <div className="mt-10 pl-6 border-l-2 border-gold max-w-2xl">
                        <p className="font-serif italic text-2xl md:text-3xl leading-snug text-ivory">
                            {QUOTES.ventures}
                        </p>
                    </div>
                </div>
            </section>

            {/* Venture sections */}
            {ORDER.map((slug, i) => {
                const v = VENTURES[slug];
                const dark = i % 2 === 1;
                return (
                    <section
                        key={slug}
                        className={dark ? "bg-charcoal text-ivory" : "bg-ivory text-charcoal"}
                    >
                        <div className="container-editorial py-24 md:py-32">
                            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
                                <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                                    <EditorialImage
                                        src={v.image}
                                        alt={v.alt || v.eyebrow}
                                        aspect="aspect-[4/5]"
                                        overlay={v.tint === "oxblood" ? "oxblood" : "dark"}
                                        position={v.position || "center"}
                                    />
                                </div>
                                <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                                    <p className={`eyebrow ${dark ? "text-gold/90" : "text-oxblood"}`}>
                                        {v.index} — {v.eyebrow}
                                    </p>
                                    <h2 className="display-2 mt-6">{v.title}</h2>
                                    <p className="mt-8 body-lead opacity-80">{v.intro}</p>
                                    {v.quote && (
                                        <div className="mt-8">
                                            <PullQuoteInline dark={dark}>{v.quote}</PullQuoteInline>
                                        </div>
                                    )}
                                    <div className="mt-10">
                                        <Link
                                            to={`/building/${slug}`}
                                            className={dark ? "btn-secondary-light" : "btn-secondary"}
                                            data-testid={`building-${slug}-read-more`}
                                        >
                                            Read more <ArrowRight size={16} strokeWidth={1.5} />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                );
            })}

            <PullQuotePrimary>{QUOTES.story}</PullQuotePrimary>
        </main>
    );
}

function VentureDetail({ slug }) {
    const v = VENTURES[slug];
    useSEO(VENTURE_SEO[slug] || SEO.building);
    return (
        <main>
            <section className="bg-charcoal text-ivory pt-36 md:pt-44 pb-20 md:pb-24 relative overflow-hidden">
                <div className="absolute inset-0 opacity-25">
                    <img src={v.image} alt="" aria-hidden="true" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-b from-charcoal/85 via-charcoal/85 to-charcoal" />
                </div>
                <div className="container-editorial relative">
                    <Link to="/building" className="inline-flex items-center gap-2 eyebrow text-gold/80 hover:text-ivory transition-colors">
                        <ArrowLeft size={14} strokeWidth={1.5} /> What I'm Building
                    </Link>
                    <p className="eyebrow text-gold/90 mt-12">{v.index} — {v.eyebrow}</p>
                    <h1 className="display-1 mt-6 max-w-4xl text-ivory">{v.title}</h1>
                    <p className="mt-10 body-lead text-ivory/75 max-w-2xl">{v.intro}</p>
                </div>
            </section>

            <section className="bg-ivory text-charcoal">
                <div className="container-narrow py-24 md:py-32 space-y-8 body-lead text-charcoal/80">
                    {v.body.map((p, idx) => (
                        <p key={idx}>{p}</p>
                    ))}
                    {v.quote && (
                        <div className="pt-6">
                            <PullQuoteInline>{v.quote}</PullQuoteInline>
                        </div>
                    )}
                </div>
            </section>

            <PullQuotePrimary dark>{QUOTES.story}</PullQuotePrimary>

            <section className="bg-ivory">
                <div className="container-editorial py-20 md:py-28">
                    <p className="eyebrow text-oxblood mb-8">Continue</p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {ORDER.filter((s) => s !== slug).map((s) => (
                            <Link
                                key={s}
                                to={`/building/${s}`}
                                className="group p-8 border border-charcoal/15 hover:bg-charcoal hover:text-ivory transition-colors"
                            >
                                <p className="eyebrow opacity-70 group-hover:text-gold transition-colors">
                                    {VENTURES[s].index}
                                </p>
                                <p className="font-serif text-3xl mt-3">{VENTURES[s].eyebrow}</p>
                                <p className="mt-2 text-sm opacity-70">{VENTURES[s].title}</p>
                            </Link>
                        ))}
                        <Link
                            to="/contact"
                            className="group p-8 border border-charcoal/15 hover:bg-charcoal hover:text-ivory transition-colors"
                        >
                            <p className="eyebrow opacity-70 group-hover:text-gold transition-colors">Get in touch</p>
                            <p className="font-serif text-3xl mt-3">Partnerships</p>
                            <p className="mt-2 text-sm opacity-70">Investors, clinicians and collaborators.</p>
                        </Link>
                    </div>
                </div>
            </section>
        </main>
    );
}
